import { useRef, useState } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { IconAlarm, IconHandStop, IconMoon, IconPlus } from '@tabler/icons-react';
import { Button, Segmented, Skeleton, StateView } from '@hana/ui';
import { HeaderTool, PageHeader } from '@hana/shell';
import { BOOKING_COVER, countdown, RL_TABS, rqStatus, shipStatus } from './booking';
import { BOOKING_P0, BOOKING_VESSELS } from './fixtures';
import type { BookingRequest } from './types';
import type { BookingPadProps } from './BookingPad';
import s from './bookingPhone.module.css';

// Mint-Booking-Phone (390) · khung 1: tiêu đề + 2 nút icon, thanh phạm vi, dải tab, P0, nhóm tàu, thẻ RQ vuốt trái. Chỉ đọc.
const DOT = { amber: 'var(--d-amber)', blue: 'var(--d-blue)', gray: 'var(--d-gray)' } as const;
const SCOPES: ReadonlyArray<{ key: 'mine' | 'cover' | 'un' | 'team'; label: string }> = [
  { key: 'mine', label: 'Của tôi' },
  { key: 'cover', label: 'Tôi thay' },
  { key: 'un', label: 'Chưa nhận' },
  { key: 'team', label: 'Cả team' },
];

const swipeFor = (r: BookingRequest): string =>
  r.tab === 'queue' ? 'Nhận' : r.tab === 'notify' ? 'Báo khách' : r.bookingNo ? 'Gửi xác nhận' : 'Lấy booking';

function Card({ r, onOpen, onAct }: { r: BookingRequest; onOpen: (r: BookingRequest) => void; onAct: (k: string) => void }) {
  const ship = shipStatus(r);
  const cy = countdown(r.cyCutoff, r.tab === 'done');
  const act = swipeFor(r);
  const [x, setX] = useState(0);
  const from = useRef<{ x: number; y: number; h: boolean | null }>({ x: 0, y: 0, h: null });
  const cyHot = r.cyWarn || (!!cy && cy.mins > 0 && cy.mins <= 360);
  return (
    <div className={s.sw} style={{ background: x < 0 ? (act === 'Báo khách' ? 'var(--warn)' : 'var(--btn)') : 'transparent' }}>
      <button type="button" tabIndex={-1} aria-hidden="true" className={s.swBtn} onClick={() => onAct(act)}>
        {act}
      </button>
      <div
        role="button"
        tabIndex={0}
        className={s.card}
        style={{ transform: `translateX(${x}px)` }}
        aria-label={`${r.id}, ${r.customer}, ${rqStatus(r)}. Vuốt trái để ${act}`}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (t) from.current = { x: t.clientX, y: t.clientY, h: null };
        }}
        onTouchMove={(e) => {
          const t = e.touches[0];
          if (!t) return;
          const dx = t.clientX - from.current.x;
          const dy = t.clientY - from.current.y;
          if (from.current.h === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) from.current.h = Math.abs(dx) > Math.abs(dy);
          if (from.current.h) setX(Math.max(-240, Math.min(0, dx + (x < 0 ? -140 : 0))));
        }}
        onTouchEnd={() => {
          if (x <= -200) onAct(act);
          setX(x <= -60 && x > -200 ? -140 : 0);
        }}
        onClick={() => (x < 0 ? setX(0) : onOpen(r))}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(r)}
      >
        <div className={s.l1}>
          <b className={s.rq}>{r.id}</b>
          <span className={s.st}>
            <span className={s.dot} style={{ background: DOT[ship.dot] }} />
            {rqStatus(r)}
          </span>
        </div>
        <div className={s.cus}>
          {r.customer}
          <span className={s.own}>{r.owner ?? 'chưa nhận'}</span>
        </div>
        <div className={s.mid}>
          {r.carrier} · {r.cont} · {r.bookingNo ? `BK ${r.bookingNo}` : 'chưa có booking'}
        </div>
        <div className={s.chips}>
          <span className={s.chip}>SI {r.siCutoff ?? '—'}</span>
          <span className={`${s.chip} ${cyHot ? s.hot : ''}`} title={`Closing CY ${r.cyCutoff ?? '—'}`}>
            CY {r.cyCutoff ?? '—'} ·<IconAlarm size={13} aria-hidden="true" />
            {cy ? cy.k : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export function BookingPhone(p: BookingPadProps) {
  const t = useT();
  const ready = p.state === 'ready';
  const p0 = BOOKING_P0[0];
  const done = p0 ? !!p.ack[p0.code] : true;
  const who = p0 ? (BOOKING_COVER[p0.pic] ? `${BOOKING_COVER[p0.pic]} (thay ${p0.pic})` : p0.pic) : '';
  const empty = ready && p.groups.every((g) => g.rows.length === 0);
  const total = p.groups.reduce((n, g) => n + g.rows.length, 0);
  const [lim, setLim] = useState(5);
  return (
    <>
      <PageHeader
        title={t('booking.a11y1')}
        actions={
          <>
            <HeaderTool label="Đổi giao diện sáng / tối" icon={IconMoon} onClick={p.onWrite} />
            <HeaderTool label="Tạo RQ" icon={IconPlus} onClick={p.onWrite} />
          </>
        }
      />
      <div className={s.scope}>
        <Segmented kind="toggle" label="Phạm vi" value={p.scope} onChange={p.onScope} items={SCOPES.map((x) => ({ key: x.key, label: x.label, n: p.ownCounts[x.key] }))} />
      </div>
      <div role="tablist" aria-label={t('booking.a11y29')} className={s.tabs}>
        {[...RL_TABS.filter((t) => t.key === 'all'), ...RL_TABS.filter((t) => t.key !== 'all')].map((t) => {
          const dis = p.lock && t.key !== 'prio';
          const on = p.tab === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} aria-disabled={dis} className={`${s.tab} ${on ? s.on : ''} ${dis ? s.dis : ''}`} onClick={() => p.onTab(t.key)}>
              {t.label}
              <span className={s.n}>{p.counts[t.key]}</span>
            </button>
          );
        })}
      </div>
      <div className={s.main}>
        {ready && p0 && (
          <div role="alert" className={s.p0}>
            <div className={s.p0Top}>
              <span className={s.p0Code}>P0 · {p0.code}</span>
              <b>{p0.k}</b>
              <span className={s.p0Left} title={`Còn ${p0.left}`}>
                <IconAlarm size={13} aria-hidden="true" />
                {p0.left}
              </span>
            </div>
            <div className={s.sm}>{p0.ref}</div>
            <div className={s.sm}>{`Đã báo ${p0.minutes}′ · 0′ ${who} → 30′ Sale ${p0.sale} → 60′ Trưởng phòng Sales`}</div>
            {done ? (
              <div className={s.ok}>{t('booking.claimedJob')}</div>
            ) : (
              <Button variant="primary" icon={<IconHandStop size={16} aria-hidden="true" />} onClick={() => p.onAck(p0.code)}>
                {t('booking.mine')}
              </Button>
            )}
          </div>
        )}
        <StateView
          state={empty ? 'empty' : p.state}
          title={empty ? 'Không có RQ trong nhóm này' : p.state === 'empty' ? 'Hàng chờ RQ đang trống' : undefined}
          action={p.state === 'error' ? <Button onClick={p.onRetry}>{t('booking.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={4} />}
        >
          {p.groups.map((g, gi) => {
            const before = p.groups.slice(0, gi).reduce((n, h) => n + h.rows.length, 0);
            const rows = g.rows.slice(0, Math.max(0, lim - before));
            if (rows.length === 0 && gi > 0) return null;
            const v = g.vessel ? BOOKING_VESSELS[g.vessel] : undefined;
            return (
              <div key={g.vessel || 'flat'} className={s.grp}>
                {v && (
                  <div className={s.gh}>
                    {g.vessel !== 'none' ? (
                      <button type="button" className={s.vsl} data-vlink={v.name.replace(/\s(\S+)$/, '|$1')} title={t('booking.a11y28')} onClick={() => p.onVessel(v.name)}>
                        {v.name}
                      </button>
                    ) : (
                      <b className={s.vsl}>{v.name}</b>
                    )}
                    <div className={s.sm}>{v.line}</div>
                    {v.change && (
                      <div className={s.chg}>
                        <b className={s.chgK}>{t('booking.reschedule')}</b> ETD <s>{v.change.from}</s> → <b>{v.change.to}</b> · {v.change.why}
                      </div>
                    )}
                  </div>
                )}
                {rows.map((r) => (
                  <Card key={r.id} r={r} onOpen={p.onOpen} onAct={p.onWrite} />
                ))}
              </div>
            );
          })}
        </StateView>
        {ready && !empty && lim < total && (
          <Button variant="ghost" onClick={() => setLim((n) => n + 5)}>
            Xem thêm {total - lim} RQ
          </Button>
        )}
        {ready && !empty && <div className={s.foot}>{total} RQ · vuốt trái thẻ để thao tác nhanh</div>}
      </div>
    </>
  );
}
